import React, { FC, useContext, useState } from 'react'
import { Column } from 'react-table'
import { t, Trans } from '@lingui/macro'

import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import { SqlOrderDirectionEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { Box, Select } from 'front-end/src/components'
import TableExtendedOld from '../../../components/common/table-extended-old/TableExtendedOld'
import { DownloadSearchCSV } from './DownloadSearchCSV'
import { getPagesCount } from '../utils'
import { IBaseOverviewFilter } from '../interfaces'
import {
  ResultSelectEnum,
  SearchOverviewOrderColumn
} from '../../../../../types/Search'
import { useSearchOverviewTableQuery } from './SearchOverviewTable.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
export interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: SearchOverviewOrderColumn
  search: string
  resultSelect: ResultSelectEnum
}

const selectOptions = [
  { value: ResultSelectEnum.NO_RESULT, label: <Trans>No results</Trans> },
  { value: ResultSelectEnum.RESULT, label: <Trans>Results</Trans> },
  { value: ResultSelectEnum.ALL, label: <Trans>All</Trans> }
]

const SearchOverviewTable: FC<{}> = () => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewTableFilter, setSearchTableFilters] = useState({
    limit: 20,
    orderColumn: SearchOverviewOrderColumn.count,
    orderDirection: SqlOrderDirectionEnum.DESC,
    resultSelect: ResultSelectEnum.NO_RESULT
  })

  const overviewTableQuery = {
    ...overviewTableFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useSearchOverviewTableQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      searchOverviewFilter: overviewTableQuery
    }
  })

  const onSearchFilterChange = (val: ResultSelectEnum) => {
    setSearchTableFilters((prevState) => {
      return {
        ...prevState,
        resultSelect: val as ResultSelectEnum
      }
    })
  }

  const getTableFilters = () => {
    return (
      <Box ml="8px">
        <Select
          options={selectOptions}
          onChange={(v) => onSearchFilterChange(v?.value)}
          value={selectOptions.find(({ value }) => {
            return value === overviewTableQuery.resultSelect
          })}
        />
      </Box>
    )
  }

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Search term`,
      accessor: 'search_term',
      className: 'overflowVisible',
      Cell: (row) => <strong className="dark">{row.value}</strong>
    },
    {
      Header: t`Times searched`,
      maxWidth: 200,
      className: 'center',
      headerClassName: 'center',
      accessor: 'count'
    },
    {
      Header: t`Avg. results`,
      maxWidth: 200,
      className: 'center',
      headerClassName: 'center',
      accessor: 'avg_results',
      sortable: false,
      Cell: (row) => (
        <span className="red">{parseFloat(row.value.toFixed(1))}</span>
      )
    },
    {
      Header: t`Last searched`,
      maxWidth: 200,
      accessor: 'last_searched',
      Cell: (props) => props.value && formatDateAndTimeShort(props.value)
    }
  ]

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.search.searchOverviewCount,
      overviewTableQuery.limit
    )
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data && data.analytics && data.analytics.search.searchOverview}
        tableHeader={t`Search`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={5}
        pages={pagesCount}
        extendFilter={getTableFilters()}
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setSearchTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as SearchOverviewOrderColumn)
                : SearchOverviewOrderColumn.count,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
        paginationContentAfter={
          data && (
            <DownloadSearchCSV
              searchOverviewFilter={overviewTableQuery}
              searchCount={data.analytics.search.searchOverviewCount}
            />
          )
        }
      />
    </div>
  )
}

export default SearchOverviewTable
